import {
  Box,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { SelectableAngleVideoPlayer } from "./SelectableAngleVideoPlayer";
import { HlsCameraVideoPlayer, HlsAngleVideoPlayer } from "./HlsVideoPlayer";
import { LiveCameraStreamPlayer, LiveStreamPlayer } from "./LiveStreamPlayer";
import { VideoControls } from "./VideoControls";
import { VideoQualityMenu } from "./VideoQualityMenu";
import { ShortLabelDetail } from "./VideoTimelineShortLabelTrack";
import {
  KeyboardSeek,
  useVideoKeyboardHandler,
} from "../hooks/useVideoKeyboardHandler";
import { DEFAULT_KEYBOARD_SEEK } from "../constants";
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import {
  VideoGrid,
  type VideoGridLayout,
  maxTileWidthForHeight,
} from "./VideoGrid";
import { DEFAULT_VIDEO_ASPECT_RATIO } from "../constants";
import { Dayjs } from "dayjs";
import { EnsureGcsTokenExists } from "components/scrapEyeVideoPlayer/GcsTokenProvider";
import { AreaMaterialSnapshot } from "components/scrapEyeLabelling/types";
import { VideoSource } from "./VideoSourceSelector";
import { ReactNode, useMemo } from "react";
import { usePermission } from "hooks/permissions";
import { useIsAdmin } from "hooks/settings";
import { useTenantTranslation } from "hooks/formatters";

export interface MultiFeedVideoPlayerBaseProps {
  startIso: string;
  endIso: string;
  events?: ScrapEyesEvent[];
  selectedEventId?: string | null;
  onEventClick?: (eventId: string) => void;
  videoStart?: Dayjs;
  colorEventsBy?: "type" | "material";
  materialNameSource?: "labelling" | "raw";
  areaMaterialSnapshots?: AreaMaterialSnapshot[];
  onMappingSnapshotClick?: (snapshot: AreaMaterialSnapshot) => void;
  selectedSnapshotTimestamp?: number | null;
  showShortLabels?: boolean;
  shortLabelDetail?: ShortLabelDetail;
  highlightedLane?: string;
  isLive?: boolean;
  showVideoControls?: boolean;
  actionButtons?: ReactNode;
  centerActionButtons?: ReactNode;
  topLeftButtons?: ReactNode;
  location?: string;
  // Marks every tile filmed at this location.
  badgeLocationName?: string;
  hotkeyScope: string;
  keyboardSeek?: KeyboardSeek;
}

export type MultiFeedVideoPlayerProps = MultiFeedVideoPlayerBaseProps & {
  gridLayout?: VideoGridLayout;
};

const LocationBadge = ({ locationName }: { locationName: string }) => {
  const { t } = useTenantTranslation();
  return (
    <Tooltip title={t("footageFromOtherLocation", { location: locationName })}>
      <Typography
        variant="body2"
        sx={{
          position: "absolute",
          bottom: 8,
          left: 8,
          zIndex: 10,
          px: 1,
          py: 0.25,
          borderRadius: 1,
          bgcolor: "warning.main",
          color: "common.black",
          fontWeight: "fontWeightMedium",
        }}
      >
        {locationName}
      </Typography>
    </Tooltip>
  );
};

/**
 * Main video player component that handles both angle-based and direct camera feeds.
 * Supports both historical playback and live streaming.
 */
export const MultiFeedVideoPlayer = ({
  source,
  startIso,
  endIso,
  events,
  selectedEventId,
  onEventClick,
  isLoading,
  videoStart,
  colorEventsBy,
  materialNameSource,
  areaMaterialSnapshots,
  onMappingSnapshotClick,
  selectedSnapshotTimestamp,
  showShortLabels,
  shortLabelDetail,
  highlightedLane,
  gridLayout,
  isLive = false,
  showVideoControls = true,
  actionButtons,
  centerActionButtons,
  topLeftButtons,
  location,
  badgeLocationName,
  hotkeyScope,
  keyboardSeek = DEFAULT_KEYBOARD_SEEK,
}: {
  source: VideoSource;
  isLoading?: boolean;
} & MultiFeedVideoPlayerProps) => {
  useVideoKeyboardHandler({ scope: hotkeyScope, seek: keyboardSeek });
  const { t } = useTenantTranslation();

  const hasLabellingPermission = usePermission([
    "video_labelling",
    "scrap_eye_manager",
  ]);
  const isAdmin = useIsAdmin();
  const showAngleNames = hasLabellingPermission || isAdmin;

  const videoElements = useMemo(() => {
    if (source.mode === "angle_group") {
      return source.videoPlayerGroups.map((groupData, groupIndex: number) => (
        <Box
          key={`group-${String(groupData.groupId)}`}
          sx={{ position: "relative", width: "100%", minWidth: 0 }}
        >
          {groupData.location_name === badgeLocationName ? (
            <LocationBadge locationName={groupData.location_name} />
          ) : null}
          <SelectableAngleVideoPlayer
            location={groupData.location_name}
            groupId={groupData.groupId}
            videoIndex={groupIndex}
            startIso={startIso}
            endIso={endIso}
            isLive={isLive}
          />
        </Box>
      ));
    }
    if (source.mode === "angle") {
      if (isLive) {
        return source.angles.map((angleData, angleIndex) => (
          <LiveStreamPlayer
            key={angleData.id}
            location={angleData.location_name}
            angle={angleData.name}
            videoIndex={angleIndex}
            deviceId={source.deviceId}
          />
        ));
      }
      return source.angles.map((angleData, angleIndex) => (
        <Box
          key={`${angleData.location_name}-${angleData.name}`}
          sx={{ position: "relative", width: "100%", minWidth: 0 }}
        >
          {angleData.location_name === badgeLocationName ? (
            <LocationBadge locationName={angleData.location_name} />
          ) : null}
          {showAngleNames ? (
            <Typography
              variant="body2"
              sx={{
                position: "absolute",
                bottom: 8,
                right: 8,
                zIndex: 10,
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                color: "white",
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                fontSize: "0.75rem",
                fontWeight: 500,
              }}
            >
              {angleData.name} ({angleData.location_name})
            </Typography>
          ) : null}
          <HlsAngleVideoPlayer
            location={angleData.location_name}
            angle={angleData.name}
            startIso={startIso}
            endIso={endIso}
            videoIndex={angleIndex}
            deviceId={source.deviceId}
          />
        </Box>
      ));
    }
    if (isLive) {
      return source.cameras.map((cameraData, cameraIndex) => (
        <LiveCameraStreamPlayer
          key={`${cameraData.deviceId}-${cameraData.physicalId}`}
          deviceId={cameraData.deviceId}
          physicalId={cameraData.physicalId}
          videoIndex={cameraIndex}
        />
      ));
    }
    return source.cameras.map((cameraData, cameraIndex) => (
      <HlsCameraVideoPlayer
        key={`${cameraData.deviceId}-${cameraData.physicalId}`}
        deviceId={cameraData.deviceId}
        physicalId={cameraData.physicalId}
        videoIndex={cameraIndex}
        startIso={startIso}
        endIso={endIso}
      />
    ));
  }, [source, startIso, endIso, isLive, showAngleNames, badgeLocationName]);

  const hasVideos = videoElements.length > 0;

  return (
    <EnsureGcsTokenExists>
      <Stack spacing={2} sx={{ flex: 1 }}>
        <Box sx={{ position: "relative" }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ position: "absolute", top: 8, left: 8, zIndex: 1000 }}
          >
            {topLeftButtons}
            {isLive || isLoading === true || !hasVideos ? null : (
              <VideoQualityMenu />
            )}
          </Stack>
          {isLoading === true ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                maxWidth: maxTileWidthForHeight(gridLayout?.maxTileHeight),
                mx: "auto",
                aspectRatio: DEFAULT_VIDEO_ASPECT_RATIO,
              }}
            >
              <CircularProgress />
            </Box>
          ) : !hasVideos ? (
            <Box
              sx={{
                height: 600,
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography variant="body1">{t("noAnglesSelected")}</Typography>
            </Box>
          ) : (
            <VideoGrid {...gridLayout}>{videoElements}</VideoGrid>
          )}
        </Box>

        {!isLive && showVideoControls ? (
          <VideoControls
            location={location}
            events={events}
            selectedEventId={selectedEventId}
            onEventClick={onEventClick}
            videoStart={videoStart}
            colorEventsBy={colorEventsBy}
            materialNameSource={materialNameSource}
            areaMaterialSnapshots={areaMaterialSnapshots}
            onMappingSnapshotClick={onMappingSnapshotClick}
            selectedSnapshotTimestamp={selectedSnapshotTimestamp}
            showShortLabels={showShortLabels}
            shortLabelDetail={shortLabelDetail}
            highlightedLane={highlightedLane}
            actionButtons={actionButtons}
            centerActionButtons={centerActionButtons}
          />
        ) : null}
      </Stack>
    </EnsureGcsTokenExists>
  );
};
